import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';

import { UserType } from '../constants';
import { clearToken } from '../utilities/auth';
import { authFetch } from '../services/authFetch';
import { getThumbnail } from '../utilities/views';
import { logOut } from '../redux/authSlice';
import ViewCard from './ViewCard';
import Footer from './Footer';

import userTips from '../pdfs/BA Guide_v3.pdf';
import './Landing.scss';

function Landing(props) {
    const dispatch = useDispatch();

    const [items, setItems] = useState([]);
    const [itemsError, setItemsError] = useState(null);

    const userType = useSelector(state => state.auth.userType);

    const handleLogOut = useCallback(() => {
        clearToken();
    
        dispatch(logOut());
    }, [dispatch]);

    useEffect(() => {
        setItemsError(null);
        
        authFetch(`${window.AnalyticsPortalConfig.apiAddress}/LandingItems`)
            .then(result => {
                if (!result.ok) {
                    if (result.status === 401) {
                        throw new Error('401');
                    }

                    throw new Error("Response not OK.");
                }
                return result.json();
                
            })
            .then((result) => {
                if (result.clientErrorMessage) {
                    setItemsError(result.clientErrorMessage);
                }
                else if (result.length === 0) {
                    setItemsError('No Views Found');
                }
                else {
                    return Promise.all(result.map(async item => 
                        Object.assign(item, { thumbnailSource: await getThumbnail(item, userType === UserType.SLIPUser) })
                    ));
                }
            })
            .then(result => {
                if (result) setItems(result);
            })
            .catch((error) => {
                if (error.message === '401') {
                    handleLogOut();

                    return;
                }

                console.error(error);

                setItemsError('Error Loading Views');
            });
    }, [handleLogOut]);

    const hasAlert = itemsError !== null || items.length === 0;

    return (
        <div className={clsx(!props.isPhone && "not-phone",
            props.isPhone && "phone",
            "landing-container d-flex flex-column w-100 p-3")}>
            <div className={clsx("item title py-4",
                !props.isPhone && "pl-5",
                props.isPhone && "pl-3")}>
                <h1>The Surplus Line Association of California Analytics Portal</h1>
            </div>
            <div className={clsx("item mt-3",
                !props.isPhone && "pl-5 pr-4 pt-4",
                props.isPhone && "pl-3 pt-3 pr-3")}>
                <h2>Welcome to the SLA's Analytics Portal!</h2>
                <p>You've arrived at the SLA's central repository for advanced analytics.</p>
                <p>The Surplus Line Association of California's data assets are vital in supporting the SLA's mission; to provide education, processing, evaluation, and dissemination of surplus line data and information to help ensure the financial integrity and stability of the surplus line market.</p>
                <p>The purpose of the SLA Analytics Portal is to support effective and innovative insights by making the SLA's data accessible and ensuring correct representation of the information to support the organization's strategic plans.</p>
            </div>
            <div className={clsx("item mt-3 flex-grow-1 d-flex",
                hasAlert && !props.isPhone && "pt-3 justify-content-center align-items-center",
                hasAlert && props.isPhone && "pt-3 justify-content-center align-items-center",
                !hasAlert && !props.isPhone && "views flex-wrap pr-4 pb-4",
                !hasAlert && props.isPhone && "views flex-column align-items-center pb-3")}>
                {itemsError !== null &&
                    <div className="alert alert-danger" role="alert">
                        {itemsError}
                    </div>
                }
                {itemsError === null && items.length === 0 &&
                    <div className="alert alert-info" role="alert">
                        Loading Views...
                    </div>
                }
                {itemsError === null && items.length > 0 && items.map((item) => 
                    <ViewCard key={item.viewId} item={item} isPhone={props.isPhone} />
                )}
            </div>
            <div className={clsx("item mt-3 pb-3",
                !props.isPhone && "pl-5 pr-4 pt-4",
                props.isPhone && "pl-3 pt-3 pr-3")}>
                <h2>New to Analytics?</h2>
                <p>Get started by downloading the SLA Analytics Portal User Tips below. It will help you navigate the analytics portal and step you through Tableau's functionality for the interactive dashboards within.</p>
                <a href={userTips} rel="noopener noreferrer" className="btn btn-primary px-5" target="_blank">User Tips</a>
            </div>
            <div>
                <Footer />
            </div>
        </div>
    );
}

export default Landing;